import { useState } from 'react';
import type { FieldDef, FormVersionDetail, FormVersionSummary } from '@jfh/intake-shared';
import { fieldTypeName } from '@jfh/intake-shared';
import { api } from '../lib/api.js';
import { Icon } from '../components/Icon.js';
import { Modal } from '../components/Modal.js';

export interface HistoryProps {
  formId: string;
  versions: FormVersionSummary[];
  currentVersionId: string | null;
  draftVersion: number;
  /** Question changes in the draft not yet live. */
  changeCount: number;
  onReview: () => void;
  /** Copy a version's questions into the draft (doesn't publish). */
  onRestore: (schema: FieldDef[], version: number) => void;
}

/** DD/MM/YYYY HH:mm (UK). */
function ukDateTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** A version's questions as an outline, step by step. */
function Outline({ schema }: { schema: FieldDef[] }) {
  const applicant = schema.filter((f) => f.audience === 'customer');
  const staff = schema.filter((f) => f.audience === 'staff' && f.type !== 'heading');
  return (
    <div style={{ fontSize: 13 }}>
      {applicant.map((f) =>
        f.type === 'heading' ? (
          <div key={f.id} style={{ fontWeight: 800, margin: '12px 0 4px' }}>
            {f.label}
          </div>
        ) : (
          <div key={f.id} style={{ padding: '3px 0 3px 12px', color: 'var(--text-1)' }}>
            {f.label}
            {f.required && <span style={{ color: 'var(--st-declined)' }}> *</span>}
            <span style={{ color: 'var(--text-3)', fontSize: 12 }}> · {fieldTypeName(f.type)}</span>
          </div>
        ),
      )}
      {staff.length > 0 && (
        <>
          <div style={{ fontWeight: 800, margin: '12px 0 4px' }}>Filled in by JFH staff</div>
          {staff.map((f) => (
            <div key={f.id} style={{ padding: '3px 0 3px 12px' }}>
              {f.label}
              <span style={{ color: 'var(--text-3)', fontSize: 12 }}> · {fieldTypeName(f.type)}</span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

/**
 * History (F9 P1): the draft, then every published version newest first, with
 * "Show questions" and "Restore as draft" (copies its questions into the
 * draft; nothing goes live until it is published).
 */
export function History({
  formId,
  versions,
  currentVersionId,
  draftVersion,
  changeCount,
  onReview,
  onRestore,
}: HistoryProps) {
  const published = versions
    .filter((v) => v.publishedAt)
    .sort((a, b) => b.version - a.version);
  const [shown, setShown] = useState<FormVersionDetail | null>(null);
  const [confirm, setConfirm] = useState<FormVersionDetail | null>(null);
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async (v: FormVersionSummary, then: (d: FormVersionDetail) => void) => {
    setLoading(v.id);
    setError(null);
    try {
      then((await api.getFormVersion(formId, v.id)).version);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not load that version');
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="scroll-y" style={{ flex: 1, padding: '22px 26px', background: 'var(--bg-app)' }}>
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <div className="card" style={{ padding: 18, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="badge pending">
              <span className="dot" /> Draft · v{draftVersion}
            </span>
            <span style={{ flex: 1, fontSize: 13, color: 'var(--text-2)' }}>
              {changeCount === 0
                ? 'No question changes waiting to go live.'
                : `${changeCount} ${changeCount === 1 ? 'change' : 'changes'} not live yet.`}
            </span>
            {changeCount > 0 && (
              <button className="btn primary sm" onClick={onReview}>
                <Icon name="bolt" size={14} /> Review &amp; publish
              </button>
            )}
          </div>
        </div>

        {error && (
          <div style={{ color: 'var(--st-declined)', fontSize: 13, marginBottom: 10 }}>{error}</div>
        )}

        {published.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
            Nothing published yet. Published versions will appear here.
          </div>
        ) : (
          published.map((v) => (
            <div key={v.id} className="card" style={{ padding: '14px 18px', marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>
                    v{v.version}{' '}
                    {v.id === currentVersionId && (
                      <span className="badge approved" style={{ marginLeft: 6 }}>
                        Live
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
                    Published {ukDateTime(v.publishedAt)}
                    {v.publishedBy ? ` by ${v.publishedBy}` : ''}
                  </div>
                </div>
                <button className="btn sm" disabled={loading === v.id} onClick={() => void load(v, setShown)}>
                  Show questions
                </button>
                {v.id !== currentVersionId && (
                  <button className="btn sm" disabled={loading === v.id} onClick={() => void load(v, setConfirm)}>
                    Restore as draft
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <Modal open={!!shown} onClose={() => setShown(null)} width={560}>
        {shown && (
          <div style={{ padding: 22 }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ flex: 1, fontWeight: 800, fontSize: 16 }}>Questions in v{shown.version}</div>
              <button className="icon-btn" onClick={() => setShown(null)} title="Close">
                <Icon name="x" size={16} />
              </button>
            </div>
            <Outline schema={shown.schema} />
          </div>
        )}
      </Modal>

      <Modal open={!!confirm} onClose={() => setConfirm(null)} width={460}>
        {confirm && (
          <div style={{ padding: 22 }}>
            <div style={{ fontWeight: 800, fontSize: 16 }}>Restore v{confirm.version} as your draft?</div>
            <p style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.5 }}>
              Your draft's questions are replaced with v{confirm.version}'s, and any draft changes are
              lost. Nothing goes live until you publish. Form settings aren't changed: they aren't kept
              per version.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn" onClick={() => setConfirm(null)}>
                Cancel
              </button>
              <button
                className="btn primary"
                onClick={() => {
                  onRestore(confirm.schema, confirm.version);
                  setConfirm(null);
                }}
              >
                Restore as draft
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
